/**
 * Goods Allocation — New / Edit / Copy dialog.
 *
 * Converts Access form `frmProcurementQryGoodAlcAdd` (Caption
 * "Procurement - Goods Allocation", btnSave / btnCancel / btnTruckID).
 * Applied inline: /convert-form (2-col grid bám Left/Top Access, Autocomplete
 * flat "<CODE> — <NAME>", Standard number right-align, DatePicker DD-MMM-YYYY),
 * /access-suggest (Request Truck ID + dup check race-safe), /responsive-style
 * (no fullScreen, grid stacks to 1 column on mobile).
 *
 * Contract routing (p_contract ↔ consignment_contract) and receipt routing
 * (receive_no/GRN ↔ virtual_wh/RDN) are mutually exclusive — picking one clears
 * the other, mirroring `p_contract_AfterUpdate` / `receive_no_AfterUpdate`.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useSnackbar } from '../../hooks/useSnackbar'
import {
  useCreateGoodAllocationMutation,
  useGoodAllocConsignmentLookupQuery,
  useGoodAllocGradeLookupQuery,
  useGoodAllocInspectionLookupQuery,
  useGoodAllocPContractLookupQuery,
  useGoodAllocReceiptGrnLookupQuery,
  useGoodAllocReceiptRdnLookupQuery,
  useGoodAllocTruckStatusLookupQuery,
  useGoodAllocWarehouseLookupQuery,
  useLazyCheckGoodAllocationTruckIdQuery,
  useLazyGetGoodAllocationFormQuery,
  useLazyGoodAllocContractSnapshotQuery,
  useLazyGoodAllocReceiptSnapshotQuery,
  useLazySuggestGoodAllocationTruckIdQuery,
  useUpdateGoodAllocationMutation,
} from '../../store/api/procurementGoodAllocationApi'
import type {
  GoodAllocationListItem,
  GoodAllocationLookupItem,
  GoodAllocationWrite,
} from '../../types/api'
import { getErrorMessage } from '../../utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
} from '../purchase/dialogStyle'

// ---------------------------------------------------------------------------
// Props — kept exactly as the Frame (ProcurementGoodAllocationListView) wires.
// ---------------------------------------------------------------------------
export interface GoodAllocationFormDialogProps {
  open: boolean
  mode: 'new' | 'edit' | 'copy'
  goodAllocationId: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

// ---------------------------------------------------------------------------
// Draft — numbers as formatted strings ("1,234.56") so Standard thousand
// separators render in inputs (Access Format = Standard).
// ---------------------------------------------------------------------------
interface DraftState {
  goods_allocation_id: string
  // contract routing (mutual exclusion)
  p_contract: string
  consignment_contract: string
  // editable
  m_date: string
  m_truck_no: string
  truck_id: string
  truck_status: string
  grade: string
  inspection: string
  to_warehouse: string
  phone_no: string
  estimated_quantity: string
  note: string
  allocating_bag: string
  allocating_quantity: string
  // receipt routing (mutual exclusion)
  receive_no: string
  virtual_wh: string
  // read-only derived (not sent)
  supplier_name: string
  grade_p: string
  pending_bag: string
  pending_quantity: string
  receive_bag: string
  receive_quantity: string
}

const EMPTY_DRAFT: DraftState = {
  goods_allocation_id: '',
  p_contract: '',
  consignment_contract: '',
  m_date: '',
  m_truck_no: '',
  truck_id: '',
  truck_status: '',
  grade: '',
  inspection: '',
  to_warehouse: '',
  phone_no: '',
  estimated_quantity: '',
  note: '',
  allocating_bag: '',
  allocating_quantity: '',
  receive_no: '',
  virtual_wh: '',
  supplier_name: '',
  grade_p: '',
  pending_bag: '',
  pending_quantity: '',
  receive_bag: '',
  receive_quantity: '',
}

// ---------------------------------------------------------------------------
// Numeric helpers (Standard → thousand sep + 2dp).
// ---------------------------------------------------------------------------
function parseNumeric(v: string | number | null | undefined): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

/** A contract_no starting with NPE → consignment; else P contract. */
function isNpe(contractNo: string | null | undefined): boolean {
  return String(contractNo ?? '').toUpperCase().startsWith('NPE')
}

type Opt = GoodAllocationLookupItem
const COL1: SxProps<Theme> = { gridColumn: { xs: '1', sm: '1' } }
const COL2: SxProps<Theme> = { gridColumn: { xs: '1', sm: '2' } }

export default function GoodAllocationFormDialog({
  open,
  mode,
  goodAllocationId,
  canEdit,
  onClose,
  onSaved,
}: GoodAllocationFormDialogProps) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [hydrating, setHydrating] = useState(false)

  // Mutations
  const [createGa, { isLoading: creating }] = useCreateGoodAllocationMutation()
  const [updateGa, { isLoading: updating }] = useUpdateGoodAllocationMutation()
  const saving = creating || updating

  // Lazy
  const [fetchForm] = useLazyGetGoodAllocationFormQuery()
  const [suggestTruckId] = useLazySuggestGoodAllocationTruckIdQuery()
  const [checkTruckId] = useLazyCheckGoodAllocationTruckIdQuery()
  const [fetchContractSnapshot] = useLazyGoodAllocContractSnapshotQuery()
  const [fetchReceiptSnapshot] = useLazyGoodAllocReceiptSnapshotQuery()

  // Lookups
  const { data: pContractData } = useGoodAllocPContractLookupQuery()
  const { data: consignmentData } = useGoodAllocConsignmentLookupQuery()
  const { data: gradeData } = useGoodAllocGradeLookupQuery()
  const { data: inspectionData } = useGoodAllocInspectionLookupQuery()
  const { data: warehouseData } = useGoodAllocWarehouseLookupQuery()
  const { data: truckStatusData } = useGoodAllocTruckStatusLookupQuery()
  const { data: grnData } = useGoodAllocReceiptGrnLookupQuery()
  const { data: rdnData } = useGoodAllocReceiptRdnLookupQuery()

  const pContractOptions = useMemo<Opt[]>(() => pContractData?.items ?? [], [pContractData])
  const consignmentOptions = useMemo<Opt[]>(() => consignmentData?.items ?? [], [consignmentData])
  const gradeOptions = useMemo<Opt[]>(() => gradeData?.items ?? [], [gradeData])
  const inspectionOptions = useMemo<Opt[]>(() => inspectionData?.items ?? [], [inspectionData])
  const warehouseOptions = useMemo<Opt[]>(() => warehouseData?.items ?? [], [warehouseData])
  const truckStatusOptions = useMemo<Opt[]>(() => truckStatusData?.items ?? [], [truckStatusData])
  const grnOptions = useMemo<Opt[]>(() => grnData?.items ?? [], [grnData])
  const rdnOptions = useMemo<Opt[]>(() => rdnData?.items ?? [], [rdnData])

  // -------------------------------------------------------------------------
  // Realtime derived pending-after-allocation (allocating_bag_AfterUpdate).
  // -------------------------------------------------------------------------
  const pendingBagNew = useMemo(() => {
    const pb = parseNumeric(draft.pending_bag)
    const ab = parseNumeric(draft.allocating_bag) ?? 0
    return pb == null ? '' : formatStandard(pb - ab)
  }, [draft.pending_bag, draft.allocating_bag])

  const pendingKgNew = useMemo(() => {
    const pq = parseNumeric(draft.pending_quantity)
    const aq = parseNumeric(draft.allocating_quantity) ?? 0
    return pq == null ? '' : formatStandard(pq - aq)
  }, [draft.pending_quantity, draft.allocating_quantity])

  // -------------------------------------------------------------------------
  // Hydrate on open. Deps strictly [open, mode, goodAllocationId].
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return
    let cancelled = false

    const init = async () => {
      if (mode === 'new' || goodAllocationId == null) {
        // Form_Load default truck_status = "Estimated" for a fresh record.
        setDraft({
          ...EMPTY_DRAFT,
          m_date: new Date().toISOString().slice(0, 10),
          truck_status: 'Estimated',
        })
        return
      }
      setHydrating(true)
      try {
        const row: GoodAllocationListItem = await fetchForm(goodAllocationId).unwrap()
        if (cancelled) return
        const cn = String(row.contract_no ?? '')
        const npe = isNpe(cn)
        const isRdn = String(row.grn_or_rdn ?? '').toUpperCase() === 'RDN'
        const receiptNo = String(row.receipt_no ?? '')

        const common: DraftState = {
          ...EMPTY_DRAFT,
          p_contract: npe ? '' : cn,
          consignment_contract: npe ? cn : '',
          m_date: String(row.m_date ?? '').slice(0, 10),
          m_truck_no: String(row.m_truck_no ?? ''),
          grade: String(row.grade ?? ''),
          inspection: String(row.inspection ?? ''),
          to_warehouse: String(row.to_warehouse ?? ''),
          phone_no: String(row.phone_no ?? ''),
          estimated_quantity: formatStandard(row.estimated_quantity),
          note: String(row.note ?? ''),
          allocating_bag: formatStandard(row.allocating_bag),
          allocating_quantity: formatStandard(row.allocating_quantity),
          receive_no: isRdn ? '' : receiptNo,
          virtual_wh: isRdn ? receiptNo : '',
          // read-only snapshot
          supplier_name: String(row.supplier ?? ''),
          grade_p: String(row.contract_quality ?? ''),
          pending_bag: formatStandard(row.pending_bag),
          pending_quantity: formatStandard(row.pending_kg),
          receive_bag: formatStandard(row.received_bag),
          receive_quantity: formatStandard(row.received_kg),
          // Copy drops the PK + truck fields (Access btnCopy behaviour).
          goods_allocation_id: mode === 'copy' ? '' : String(row.id ?? ''),
          truck_id: mode === 'copy' ? '' : String(row.truck_id ?? ''),
          truck_status: mode === 'copy' ? 'Estimated' : String(row.truck_status ?? ''),
        }
        setDraft(common)
      } catch (err) {
        if (!cancelled) snack.error(getErrorMessage(err, 'Failed to load goods allocation'))
      } finally {
        if (!cancelled) setHydrating(false)
      }
    }

    void init()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, goodAllocationId])

  // -------------------------------------------------------------------------
  // Field setters + mutual-exclusion cascades.
  // -------------------------------------------------------------------------
  const set = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  // Contract pick → fetch snapshot (supplier_name/grade/pending) — Access
  // p_contract_AfterUpdate / consignment_contract_AfterUpdate. Empty → clear.
  const applyContractSnapshot = async (contractNo: string, kind: 'p' | 'consignment') => {
    if (!contractNo) {
      setDraft((prev) => ({ ...prev, supplier_name: '', grade_p: '', pending_bag: '', pending_quantity: '' }))
      return
    }
    try {
      const snap = await fetchContractSnapshot({ contractNo, kind }).unwrap()
      setDraft((prev) => ({
        ...prev,
        supplier_name: snap.supplier_name ?? '',
        grade_p: snap.grade ?? '',
        pending_bag: formatStandard(snap.pending_bag),
        pending_quantity: formatStandard(snap.pending_quantity),
      }))
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to load contract info'))
    }
  }

  // Receipt pick → fetch snapshot (receive_bag/quantity) — Access
  // receive_no_AfterUpdate (GRN) / virtual_wh_AfterUpdate (RDN). Empty → clear.
  const applyReceiptSnapshot = async (receiptNo: string, kind: 'GRN' | 'RDN') => {
    if (!receiptNo) {
      setDraft((prev) => ({ ...prev, receive_bag: '', receive_quantity: '' }))
      return
    }
    try {
      const snap = await fetchReceiptSnapshot({ receiptNo, kind }).unwrap()
      setDraft((prev) => ({
        ...prev,
        receive_bag: formatStandard(snap.receive_bag),
        receive_quantity: formatStandard(snap.receive_quantity),
      }))
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to load receipt info'))
    }
  }

  // p_contract ↔ consignment_contract (p_contract_AfterUpdate clears the other + loads snapshot).
  const handlePickPContract = (value: string) => {
    setDraft((prev) => ({ ...prev, p_contract: value, consignment_contract: value ? '' : prev.consignment_contract }))
    void applyContractSnapshot(value, 'p')
  }

  const handlePickConsignment = (value: string) => {
    setDraft((prev) => ({ ...prev, consignment_contract: value, p_contract: value ? '' : prev.p_contract }))
    void applyContractSnapshot(value, 'consignment')
  }

  // receive_no (GRN) ↔ virtual_wh (RDN) (receive_no_AfterUpdate clears the other + loads snapshot).
  const handlePickReceiveNo = (value: string) => {
    setDraft((prev) => ({ ...prev, receive_no: value, virtual_wh: value ? '' : prev.virtual_wh }))
    void applyReceiptSnapshot(value, 'GRN')
  }

  const handlePickVirtualWh = (value: string) => {
    setDraft((prev) => ({ ...prev, virtual_wh: value, receive_no: value ? '' : prev.receive_no }))
    void applyReceiptSnapshot(value, 'RDN')
  }

  const handleNumericBlur = (key: keyof DraftState) => () => {
    setDraft((prev) => {
      const formatted = formatStandard(prev[key])
      if (formatted === prev[key]) return prev
      return { ...prev, [key]: formatted }
    })
  }

  // -------------------------------------------------------------------------
  // Request Truck ID (btnTruckID_Click) — confirm if already has one.
  // -------------------------------------------------------------------------
  const [requesting, setRequesting] = useState(false)
  const handleRequestTruckId = async () => {
    if (!canEdit || requesting) return
    if (draft.truck_id.trim()) {
      const ok = window.confirm('This record had TruckID. Do you want to request a new number?')
      if (!ok) return
    }
    setRequesting(true)
    try {
      const res = await suggestTruckId().unwrap()
      set('truck_id', String(res.truck_id ?? ''))
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to request Truck ID'))
    } finally {
      setRequesting(false)
    }
  }

  // -------------------------------------------------------------------------
  // Save (btnSave_Click)
  // -------------------------------------------------------------------------
  const savingRef = useRef(false)
  const handleSave = async () => {
    if (!canEdit || saving || hydrating || savingRef.current) return

    // Access errCheck = 2: both contracts chosen → block (verbatim message).
    if (draft.p_contract && draft.consignment_contract) {
      snack.warning('Please choose only P contract or Consignment!')
      return
    }

    savingRef.current = true
    try {
      // Approved + empty truck_id → auto-request (btnSave_Click branch).
      let truckId = draft.truck_id.trim()
      if (draft.truck_status === 'Approved' && !truckId) {
        try {
          const res = await suggestTruckId().unwrap()
          truckId = String(res.truck_id ?? '')
          set('truck_id', truckId)
        } catch (err) {
          snack.error(getErrorMessage(err, 'Failed to request Truck ID'))
          return
        }
      }

      // Duplicate truck_id check (errCheck = 4).
      if (truckId) {
        const dup = await checkTruckId({
          truckId,
          excludeId: goodAllocationId ?? undefined,
        }).unwrap()
        if (dup.exists) {
          snack.warning(`Truck ID is existed! Please type with Truck ID = '${dup.suggested ?? ''}' !`)
          return
        }
      }

      // Derive contract_no (p_contract priority) + receipt routing.
      const contractNo = (draft.p_contract || draft.consignment_contract || '').trim()
      let receiptNo = ''
      let grnOrRdn = ''
      if (draft.receive_no.trim()) {
        receiptNo = draft.receive_no.trim()
        grnOrRdn = 'GRN'
      } else if (draft.virtual_wh.trim()) {
        receiptNo = draft.virtual_wh.trim()
        grnOrRdn = 'RDN'
      }

      const body: GoodAllocationWrite = {
        ...(isEdit && goodAllocationId != null ? { goods_allocation_id: goodAllocationId } : {}),
        contract_no: contractNo || null,
        m_date: draft.m_date || null,
        m_truck_no: draft.m_truck_no || null,
        estimated_quantity: parseNumeric(draft.estimated_quantity),
        quality_code: draft.grade || null,
        inspection: draft.inspection || null,
        phone_no: draft.phone_no || null,
        to_warehouse: draft.to_warehouse || null,
        receipt_no: receiptNo || null,
        grn_or_rdn: grnOrRdn || null,
        allocating_bag: parseNumeric(draft.allocating_bag),
        allocating_quantity: parseNumeric(draft.allocating_quantity),
        note: draft.note || null,
        truck_id: truckId || null,
        truck_status: draft.truck_status || null,
      }

      if (isEdit && goodAllocationId != null) {
        await updateGa({ id: goodAllocationId, body }).unwrap()
        snack.success('Successfully Updated')
      } else {
        await createGa(body).unwrap()
        snack.success('Successfully Added')
      }
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save goods allocation'))
    } finally {
      savingRef.current = false
    }
  }

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  const titleText =
    mode === 'new'
      ? 'Inbound Logistics - Goods Allocation - New'
      : mode === 'copy'
        ? 'Inbound Logistics - Goods Allocation - Copy'
        : 'Inbound Logistics - Goods Allocation - Edit'

  const canSave = canEdit && !saving && !hydrating

  /** Flat Autocomplete "<CODE> — <NAME>", collapse when code === label. */
  const renderCombo = (params: {
    label: string
    value: string
    options: Opt[]
    onPick: (v: string) => void
    sx?: SxProps<Theme>
    disabled?: boolean
  }) => {
    const { label, value, options, onPick, sx, disabled } = params
    const sel = options.find((o) => o.value === value) ?? (value ? { value, label: value } : null)
    const merged = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
    return (
      <Autocomplete
        size="small"
        sx={sx}
        options={merged}
        value={sel}
        disabled={disabled}
        onChange={(_e, v) => onPick(v?.value ?? '')}
        getOptionLabel={(o) => {
          if (!o) return ''
          const code = String(o.value ?? '')
          const lab = String(o.label ?? '')
          if (!lab || lab === code) return code
          return `${code} — ${lab}`
        }}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        renderInput={(p) => <TextField {...p} label={label} />}
      />
    )
  }

  /** Read-only display TextField (shaded). */
  const roField = (key: keyof DraftState | { text: string }, label: string, sx?: SxProps<Theme>, num = false) => {
    const val = typeof key === 'object' ? key.text : draft[key]
    return (
      <TextField
        label={label}
        size="small"
        fullWidth
        value={val}
        InputProps={{ readOnly: true }}
        InputLabelProps={{ shrink: true }}
        inputProps={num ? numericInputProps : undefined}
        sx={[roSx, ...(num ? [numSx] : []), ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])] as SxProps<Theme>}
      />
    )
  }

  /** Editable numeric field. */
  const numField = (key: keyof DraftState, label: string, sx?: SxProps<Theme>) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      type="text"
      value={draft[key]}
      onChange={(e) => set(key, e.target.value)}
      onBlur={handleNumericBlur(key)}
      disabled={!canEdit}
      inputProps={numericInputProps}
      sx={[numSx, ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])] as SxProps<Theme>}
    />
  )

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title={titleText} sx={dialogTitleTextSx}>
          {titleText}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
            Read-only — you don&apos;t have edit permission for Goods Allocation.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            columnGap: 1.5,
            rowGap: 1.25,
            alignItems: 'start',
          }}
        >
          {/* Row 1 — GoodAlc ID (edit only) */}
          {isEdit && (
            <TextField
              label="GoodAlc ID"
              size="small"
              fullWidth
              value={draft.goods_allocation_id}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={[roSx, COL1, { gridRow: { sm: '1' } }] as SxProps<Theme>}
            />
          )}

          {/* Row 2 — m_date | m_truck_no */}
          <Box sx={[COL1, { gridRow: { sm: '2' } }] as SxProps<Theme>}>
            <DatePicker
              label="M.Date"
              format="DD-MMM-YYYY"
              disabled={!canEdit}
              value={draft.m_date ? dayjs(draft.m_date) : null}
              onChange={(d) => set('m_date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true } },
              }}
            />
          </Box>
          <TextField
            label="M.Truck No"
            size="small"
            fullWidth
            value={draft.m_truck_no}
            onChange={(e) => set('m_truck_no', e.target.value)}
            disabled={!canEdit}
            sx={[COL2, { gridRow: { sm: '2' } }] as SxProps<Theme>}
          />

          {/* Row 3 — truck_id + Get TruckID button (beside, Access btnTruckID Left=6528) | grade */}
          <Box
            sx={[COL1, { gridRow: { sm: '3' }, display: 'flex', gap: 1, alignItems: 'center' }] as SxProps<Theme>}
          >
            <TextField
              label="Truck ID"
              size="small"
              fullWidth
              value={draft.truck_id}
              onChange={(e) => set('truck_id', e.target.value)}
              disabled={!canEdit}
            />
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => void handleRequestTruckId()}
              disabled={!canEdit || requesting}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {requesting ? <CircularProgress size={14} color="inherit" /> : 'Get TruckID'}
            </Button>
          </Box>
          {renderCombo({
            label: 'Grade',
            value: draft.grade,
            options: gradeOptions,
            onPick: (v) => set('grade', v),
            disabled: !canEdit,
            sx: [COL2, { gridRow: { sm: '3' } }] as SxProps<Theme>,
          })}

          {/* Row 4 — truck_status */}
          {renderCombo({
            label: 'Truck Status',
            value: draft.truck_status,
            options: truckStatusOptions,
            onPick: (v) => set('truck_status', v),
            disabled: !canEdit,
            sx: [COL1, { gridRow: { sm: '4' } }] as SxProps<Theme>,
          })}

          {/* Row 5 — inspection (full-width — Access Left=2172 Width=12084 spans both cols) */}
          {renderCombo({
            label: 'Inspection',
            value: draft.inspection,
            options: inspectionOptions,
            onPick: (v) => set('inspection', v),
            disabled: !canEdit,
            sx: { gridColumn: '1 / -1', gridRow: { sm: '5' } },
          })}

          {/* Row 6 — to_warehouse */}
          {renderCombo({
            label: 'To Warehouse',
            value: draft.to_warehouse,
            options: warehouseOptions,
            onPick: (v) => set('to_warehouse', v),
            disabled: !canEdit,
            sx: [COL1, { gridRow: { sm: '6' } }] as SxProps<Theme>,
          })}

          {/* Row 7 — estimated_quantity | phone_no */}
          {numField('estimated_quantity', 'Estimated Qty', [COL1, { gridRow: { sm: '7' } }] as SxProps<Theme>)}
          <TextField
            label="Phone Number"
            size="small"
            fullWidth
            value={draft.phone_no}
            onChange={(e) => set('phone_no', e.target.value)}
            disabled={!canEdit}
            sx={[COL2, { gridRow: { sm: '7' } }] as SxProps<Theme>}
          />

          {/* Row 8 — note (multiline, full-width — Access Left=2172 Width=12084 spans both cols) */}
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={draft.note}
            onChange={(e) => set('note', e.target.value)}
            disabled={!canEdit}
            sx={{ gridColumn: '1 / -1', gridRow: { sm: '8' } }}
          />

          {/* Row 9 — allocating_bag | allocating_quantity */}
          {numField('allocating_bag', 'Allocating Bag', [COL1, { gridRow: { sm: '9' } }] as SxProps<Theme>)}
          {numField('allocating_quantity', 'Allocating Kg', [COL2, { gridRow: { sm: '9' } }] as SxProps<Theme>)}

          {/* Row 10 — p_contract | consignment_contract (mutual exclusion) */}
          {renderCombo({
            label: 'P Contract',
            value: draft.p_contract,
            options: pContractOptions,
            onPick: handlePickPContract,
            disabled: !canEdit,
            sx: [COL1, { gridRow: { sm: '10' } }] as SxProps<Theme>,
          })}
          {renderCombo({
            label: 'Consignment P',
            value: draft.consignment_contract,
            options: consignmentOptions,
            onPick: handlePickConsignment,
            disabled: !canEdit,
            sx: [COL2, { gridRow: { sm: '10' } }] as SxProps<Theme>,
          })}

          {/* Row 11 — contract exclusivity hint (COL1) | final_contract Grade (COL2, readonly) */}
          <Typography
            variant="caption"
            sx={[COL1, { gridRow: { sm: '11' }, color: 'text.secondary', mt: -0.5 }] as SxProps<Theme>}
          >
            * Only choose P contract or Consignment P
          </Typography>

          {/* Row 12 — supplier_name | grade_p (read-only) */}
          {roField('supplier_name', 'Supplier Name', [COL1, { gridRow: { sm: '12' } }] as SxProps<Theme>)}
          {roField('grade_p', 'Grade', [COL2, { gridRow: { sm: '12' } }] as SxProps<Theme>)}

          {/* Row 13 — pending_bag | pending_quantity (read-only) */}
          {roField('pending_bag', 'Pending Bag', [COL1, { gridRow: { sm: '13' } }] as SxProps<Theme>, true)}
          {roField('pending_quantity', 'Pending Quantity', [COL2, { gridRow: { sm: '13' } }] as SxProps<Theme>, true)}

          {/* Row 14 — receive_no (GRN) | virtual_wh (RDN) (mutual exclusion) */}
          {renderCombo({
            label: 'Receive No',
            value: draft.receive_no,
            options: grnOptions,
            onPick: handlePickReceiveNo,
            disabled: !canEdit,
            sx: [COL1, { gridRow: { sm: '14' } }] as SxProps<Theme>,
          })}
          {renderCombo({
            label: 'Virtual WH',
            value: draft.virtual_wh,
            options: rdnOptions,
            onPick: handlePickVirtualWh,
            disabled: !canEdit,
            sx: [COL2, { gridRow: { sm: '14' } }] as SxProps<Theme>,
          })}

          {/* Row 15 — receipt exclusivity hint (COL1) | final_receipt Grade (COL2, readonly) */}
          <Typography
            variant="caption"
            sx={[COL1, { gridRow: { sm: '15' }, color: 'text.secondary', mt: -0.5 }] as SxProps<Theme>}
          >
            * Only choose Receive No or Virtual WH
          </Typography>

          {/* Row 16 — receive_bag | receive_quantity (read-only) */}
          {roField('receive_bag', 'No of bag', [COL1, { gridRow: { sm: '16' } }] as SxProps<Theme>, true)}
          {roField('receive_quantity', 'Quantity', [COL2, { gridRow: { sm: '16' } }] as SxProps<Theme>, true)}

          {/* Row 17 — pending after allocation (derived, read-only) — Access bottom row (Top=7788) */}
          {roField({ text: pendingBagNew }, 'Pending Bag (after)', [COL1, { gridRow: { sm: '17' } }] as SxProps<Theme>, true)}
          {roField({ text: pendingKgNew }, 'Pending Kg (after)', [COL2, { gridRow: { sm: '17' } }] as SxProps<Theme>, true)}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
